import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pause, Play, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtTime } from "@/lib/music";

const Bars = ({ peaks, progress, onSeek }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.clientWidth * dpr;
    c.height = c.clientHeight * dpr;
    const g = c.getContext("2d");
    const max = Math.max(0.01, ...peaks);
    const bw = c.width / peaks.length;
    peaks.forEach((p, i) => {
      const h = Math.max(2 * dpr, (p / max) * c.height * 0.95);
      g.fillStyle = i / peaks.length <= progress ? "#E11D48" : "#3d3346";
      g.fillRect(i * bw, (c.height - h) / 2, Math.max(1, bw * 0.6), h);
    });
  }, [peaks, progress]);
  return (
    <canvas
      ref={ref}
      data-testid="listen-waveform"
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onSeek((e.clientX - r.left) / r.width);
      }}
      className="w-full h-28 cursor-pointer"
    />
  );
};

export default function Listen() {
  const { shareId } = useParams();
  const audio = useRef(null);
  const [share, setShare] = useState(null);
  const [src, setSrc] = useState("");
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);

  useEffect(() => {
    let url = "";
    api
      .getShare(shareId)
      .then(async (s) => {
        setShare(s);
        const data = await api.fetchAudio(s.file_id);
        url = URL.createObjectURL(new Blob([data], { type: "audio/wav" }));
        setSrc(url);
      })
      .catch((e) => setError(errMsg(e)));
    return () => url && URL.revokeObjectURL(url);
  }, [shareId]);

  const toggle = () => (audio.current.paused ? audio.current.play() : audio.current.pause());
  const duration = share?.duration || 1;

  return (
    <div className="min-h-screen bg-[#080708] flex flex-col" data-testid="listen-page">
      <header className="max-w-4xl w-full mx-auto px-6 h-20 flex items-center">
        <Link to="/" className="font-gothic text-lg tracking-[0.15em] text-amber-100/90">VOX <span className="text-[#c92a3e]">✦</span> NOCTURNA</Link>
      </header>
      <main className="flex-1 flex items-center">
        <div className="max-w-4xl w-full mx-auto px-6 pb-24">
          {error ? (
            <p className="font-cormorant italic text-2xl text-stone-400" data-testid="listen-error">{error}</p>
          ) : !share ? (
            <Loader2 className="animate-spin text-[#c92a3e]" />
          ) : (
            <div className="stone-panel arch-top p-10 rise tube-glow">
              <p className="eyebrow text-[#D99B43]">✦ A hymn from the sanctum ✦</p>
              <h1 className="font-gothic text-4xl sm:text-5xl uppercase tracking-tight text-amber-100/90 mt-4 break-words" data-testid="listen-title">{share.title}</h1>
              {share.artist && <p className="font-cormorant italic text-2xl text-stone-300 mt-2" data-testid="listen-artist">by {share.artist}</p>}
              <div className="flex items-center gap-6 mt-10">
                <button
                  data-testid="listen-play-btn"
                  onClick={toggle}
                  disabled={!src}
                  className="w-16 h-16 shrink-0 rounded-full bg-[#8B1E2B] hover:bg-[#a52535] flex items-center justify-center text-stone-100 transition-colors disabled:opacity-40 shadow-[0_0_24px_rgba(201,42,62,0.45)]"
                >
                  {!src ? <Loader2 className="animate-spin" /> : playing ? <Pause /> : <Play className="ml-1" />}
                </button>
                <div className="flex-1 min-w-0">
                  <Bars peaks={share.peaks.length ? share.peaks : [0.2]} progress={time / duration} onSeek={(f) => audio.current && (audio.current.currentTime = f * duration)} />
                  <p className="font-mono-vox text-xs text-stone-500 mt-2 flex justify-between" data-testid="listen-time">
                    <span>{fmtTime(time)}</span>
                    <span>{fmtTime(duration)}</span>
                  </p>
                </div>
              </div>
              <audio ref={audio} src={src || undefined} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)} />
              <p className="text-xs text-stone-500 mt-10">Made with Vox Nocturna · <Link to="/" className="text-amber-300/80 hover:underline" data-testid="listen-cta-link">Make your own harmonies free</Link></p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
